import React from 'react';
import PropTypes from 'prop-types';

import { Icon, Translate, SafeAnchor, RatingIcons } from '@hydra/common/components';
import ReviewCriteriaCTA from '@hydra/common/components/CTA/ReviewCriteriaCTA';
import ReviewSkeleton from '@hydra/common/components/ReviewSkeleton';
import { Criterium, CriteriumSkeleton, CriteriumTable } from '@hydra/common/criterium';
import { useReviewContext, usePdfTemplateContext } from '@hydra/common/context';

export const PjBacklinksQuality = ({ criterium }) => {
  const { isFree, isPdf, variant } = useReviewContext();
  const { pdfTheme } = usePdfTemplateContext();
  const isModern = isPdf && pdfTheme === 'modern';
  if (isFree) {
    return (
      <Criterium criterium={{ type: 'pj_backlinks_quality', score: null, importance: 0, solvability: 0 }} clickable={false} align='center'>
        <ReviewCriteriaCTA
          text={<Translate component='div' content='pj_backlinks_quality_cta_text' />}
          event='quality'
        />
      </Criterium>
    );
  }

  if (!criterium) {
    return (
      <Criterium criterium={{ type: 'pj_backlinks_quality', score: null, importance: 0, solvability: 0 }} clickable={false}>
        <ReviewSkeleton height={256} />
      </Criterium>
    );
  }

  const { data } = criterium;

  const headings = [
    '',
    <Translate content='pj_backlinks_quality_table_origin' key='pj_backlinks_quality_table_origin' />,
    <Translate content='pj_backlinks_quality_table_target' key='pj_backlinks_quality_table_target' />,
    <Translate content='pj_backlinks_quality_table_anchor' key='pj_backlinks_quality_table_anchor' />,
    <Translate content='pj_backlinks_quality_table_quality' key='pj_backlinks_quality_table_quality' />
  ];

  const hasBacklinks = data.backlinks && data.backlinks.length > 0;

  const rows = hasBacklinks
    ? data.backlinks
        .filter(backlink => !backlink.flagDeleted)
        .map(backlink => {
          const starCount = Math.min(Math.floor(backlink.qualityScore), 3);
          return [
            backlink.flagNoFollow ? <Icon type='nofollow' color='grey' size='xs' /> : null,
            <SafeAnchor href={`http://${backlink.url}`} rel='nofollow noopener noreferrer' target='_blank' key={backlink.url}>{backlink.url}</SafeAnchor>,
            <SafeAnchor href={`http://${backlink.targetURL}`} rel='nofollow noopener noreferrer' target='_blank' key={backlink.targetURL}>{backlink.targetURL}</SafeAnchor>,
            <span key={backlink.anchorText}>{backlink.anchorText}</span>,
            <RatingIcons type='rating' size={isModern ? 'md' : 'xs'} rating={starCount} key='rating' />
          ];
        })
    : [];

  const content = (
    <>
      {hasBacklinks
        ? (
          <CriteriumTable
            fixedColumns={[10, 30, 20, 30, 25]}
            head={headings}
            rows={rows}
            thinHead
            striped
            nowrap
          />
          )
        : (variant === 'nocrit' ? <CriteriumSkeleton width='70%' /> : <Translate content='not_found' />)}
    </>
  );

  return <Criterium criterium={criterium}>{content}</Criterium>;
};

PjBacklinksQuality.propTypes = {
  criterium: PropTypes.shape({
    data: PropTypes.shape({
      count: PropTypes.number,
      backlinks: PropTypes.arrayOf(
        PropTypes.shape({
          refDomain: PropTypes.string,
          url: PropTypes.string,
          anchorText: PropTypes.string,
          targetURL: PropTypes.string,
          flagNoFollow: PropTypes.number,
          flagDeleted: PropTypes.number,
          sourceCitationFlow: PropTypes.number,
          sourceTrustFlow: PropTypes.number,
          refDomainCitationFlow: PropTypes.number,
          refDomainTrustFlow: PropTypes.number,
          qualityScore: PropTypes.number
        })
      )
    })
  })
};

export default React.memo(PjBacklinksQuality);
